import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import DayPicker from 'react-day-picker/DayPicker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import MomentLocaleUtils from 'react-day-picker/moment';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import moment from 'moment';
import styled from 'styled-components';

import CalendarBottomSheet from '@rover/components/src/BottomSheet/CalendarBottomSheet';
import InlineErrorDateInput, {
  errorMessageType,
} from '@rover/components/src/formFields/InlineErrorDateInput/InlineErrorDateInput';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import Calendar from '@rover/icons/svg/calendar.svg';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Heading, Paragraph } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import type { Props as LabelAndErrorFormGroupProps } from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import LabelAndErrorFormGroup from '@rover/react-lib/src/components/utils/LabelAndErrorFormGroup';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import useOnClickOutside from '@rover/react-lib/src/hooks/useOnClickOutside';
import getMaxRequestDate from '@rover/react-lib/src/utils/getMaxRequestDate';
import { DateRangeField } from '@rover/types/src/datetime/DateRange';
import type { Day } from '@rover/types/src/datetime/Day';
import Enum from '@rover/types/src/Enum';
import getDateTimeFormatMapForLocale from '@rover/utilities/datetime';

import '@rover/utilities/allMomentLocales';
import 'react-day-picker/lib/style.css';

import MultiDatePicker from '../MultiDatePicker';

import createRenderDayForHolidays from './createRenderDayForHolidays';
import DatePickerCaption from './DatePickerCaption';
import DatePickerNavbar from './DatePickerNavbar';
import DatePickerStylingWrapper from './DatePickerStylingWrapper';

export const StyledIcon = styled(Calendar)<{ iconStyle?: React.CSSProperties }>`
  position: absolute;
  height: ${({ iconStyle, theme }) => iconStyle?.height ?? theme.sizes['4x']};
  width: ${({ iconStyle, theme }) => iconStyle?.width ?? theme.sizes['4x']};
  right: ${({ iconStyle, theme }) => iconStyle?.right ?? theme.sizes['4x']};
  top: ${({ theme }) => theme.sizes['3x']};
  fill: ${({ theme }): string => theme.textColor.tertiary};
  pointer-events: none;
`;

const StyledCloseIcon = styled(Close)`
  height: ${({ theme }): string => theme.sizes['4x']};
  width: ${({ theme }): string => theme.sizes['4x']};
  fill: ${({ theme }): string => theme.textColor.tertiary};
  pointer-events: none;
`;

export class CalendarDirection extends Enum<string> {
  static UP = new CalendarDirection('up');

  static DOWN = new CalendarDirection('down');

  static FLEX = new CalendarDirection('flex');

  static isLocked = true;
}

export type Props = Omit<LabelAndErrorFormGroupProps, 'placeholder'> & {
  allowKeyboardInput?: boolean;
  asCalendar?: boolean;
  calendarDirection?: CalendarDirection;
  className?: string;
  date?: DateRangeField;
  holidays?: Date[];
  id?: string;
  initialMonth?: Date;
  isDayBlocked?: (arg0: Date) => boolean;
  locale: Locale;
  maxDate?: DateRangeField;
  minDate?: DateRangeField;
  // `modifiers` is defined by react-day-picker and can literally be anything
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modifiers?: Record<string, any>;
  onChange: (arg0: Date | undefined) => void;
  placeholder?: string;
  newCalendarInteraction?: boolean;
  serviceSubtitle?: string;
  serviceName?: string;
  disableValidation?: boolean;
  isMobileBrowser?: boolean;
  validationType: 'popover' | 'inline';
  inputStyle?: React.CSSProperties;
  iconStyle?: React.CSSProperties;
  onClickFromSearch?: () => void;
  ariaLabelledBy?: string;
  onInputBlur?: (e?: React.FocusEvent<HTMLDivElement, Element>, isDateValid?: boolean) => void;
};

export function parseDatePopoverErrors(validationErrors, isInExperiment): undefined | string {
  /* The component receives errors from MultiDateTimePicker, which contain both date and time errors.
  To show the correct message on the calendar, we need to distinguish between them: one error = date validation,
  list of errors = time picker's validation. In the future, popovers will be deprecated and date/time components
  separated - remember to get rid of this function if newCalendarInteraction experiment wins! */
  if (isInExperiment) {
    return undefined;
  }
  return Array.isArray(validationErrors) ? undefined : validationErrors;
}

type DayPickerInputProps = React.ComponentProps<typeof DayPickerInput>;

const DATE_PICKER_CALENDAR_HEIGHT = 320;

export type DatePickerHandle = {
  showDatePicker: () => void;
};

type KeyboardEvent = {
  key: string;
  preventDefault(): void;
};

function checkDateErrors(selectedDate, i18n: I18n, disableValidation = false): string | undefined {
  if (disableValidation) return undefined;

  if (!selectedDate) {
    return i18n._(msg`Select a start date`);
  }
  return undefined;
}

const DatePicker = forwardRef<DatePickerHandle, Props>(function DatePicker(
  {
    allowKeyboardInput = true,
    asCalendar = false,
    className = '',
    date,
    id = '',
    isDayBlocked,
    locale,
    minDate = new Date(),
    maxDate,
    initialMonth,
    onChange,
    placeholder = 'Select a Date',
    calendarDirection = CalendarDirection.FLEX,
    holidays = [],
    modifiers,
    validationError: validationErrorProp,
    validationType,
    newCalendarInteraction = false,
    serviceName = '',
    serviceSubtitle = '',
    disableValidation = false,
    isMobileBrowser = false,
    inputStyle,
    iconStyle,
    onClickFromSearch,
    ariaLabelledBy = '',
    onInputBlur,
    ...other
  }: Props,
  externalDatepickerRef
) {
  let focusTimeout: ReturnType<typeof setTimeout> | undefined;
  let blurTimeout: ReturnType<typeof setTimeout> | undefined;
  let observer: IntersectionObserver | undefined;
  let potentialValidationError: errorMessageType;
  let isObserving = false;

  const { t, i18n } = useLingui();
  const { textColor, background, sizes, borderWidth, radii, colors } = useTheme();
  const [userIsTouching, setUserIsTouching] = useState(false);
  const [validationError, setValidationError] = useState<errorMessageType>(undefined);
  const [calendarIsOpen, setCalendarIsOpen] = useState(false);
  const [calendarDropUp, setCalendarDropUp] = useState(false);
  const [calendarHeightOffset, setCalendarHeightOffset] = useState(40);
  const [calendarInset, setCalendarInset] = useState(0);
  const [touchingWhileOpen, setTouchingWhileOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(date || null);

  useEffect(() => {
    if (date) setSelectedDate(date);
  }, [date]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const dayPickerRef = useRef<DayPickerInput>(null);
  useOnClickOutside(wrapperRef, (event) => {
    setShowCalendar(false);
    // The legacy overlay only hides on input blur, which has already fired once focus
    // moved into the overlay. Touch closes via handleOutsideTouchEnd so a scroll doesn't.
    if (!newCalendarInteraction && event?.type === 'mousedown') {
      dayPickerRef.current?.hideDayPicker();
    }
    if (showCalendar) {
      if (!disableValidation) setValidationError(checkDateErrors(selectedDate, i18n));
    }
  });
  const inputPassedRootMargin = (entries: IntersectionObserverEntry[]): void => {
    if (!isObserving) return; // This prevents the function from executing if it's still in the JS event loop queue when unobserve is called

    entries.forEach((entry) => {
      setCalendarDropUp(
        calendarDirection !== CalendarDirection.DOWN && entry.intersectionRatio < 1
      );
    });
  };

  const getObserver = (): IntersectionObserver =>
    new IntersectionObserver(inputPassedRootMargin, {
      root: null,
      rootMargin: `0px 0px -${DATE_PICKER_CALENDAR_HEIGHT}px 0px`,
      threshold: 1.0,
    });

  const observe = (): void => {
    isObserving = true;
    observer = getObserver();
    if (!(wrapperRef.current instanceof HTMLElement)) return;
    observer.observe(wrapperRef.current);
    const inputHeight = wrapperRef.current.getBoundingClientRect().height;
    setCalendarHeightOffset(inputHeight);
  };

  const unobserve = (): void => {
    if (observer && wrapperRef?.current) {
      observer.unobserve(wrapperRef.current);
      isObserving = false;
    }
  };

  useEffect(() => {
    return (): void => {
      unobserve();
      clearTimeout(focusTimeout);
      clearTimeout(blurTimeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dayPickerLateralAdjust = (): void => {
    const windowWidth = window.innerWidth;
    const inputEl = wrapperRef?.current;
    const wrapperEl = inputEl?.getElementsByClassName('DayPickerInput-Overlay')[0];
    if (!inputEl || !wrapperEl) return;
    const { width: calendarWidth } = wrapperEl.getBoundingClientRect();
    const { left: inputLeft, right: inputRight } = inputEl.getBoundingClientRect();
    const calendarRight = inputLeft + calendarWidth;
    let inset = 0;

    // If the calendar goes off the right side of the screen, try the left instead
    if (calendarRight >= windowWidth) {
      const overshootRight = calendarRight - windowWidth;
      inset = calendarRight - inputRight;
      const overshootLeft = inset - inputLeft;

      // If the calendar overshoots the left more than the right, reset to right
      if (overshootLeft > overshootRight) {
        inset = 0;
      }
    }

    // Make sure the calendar is inside the right side of the screen
    if (calendarRight - inset > windowWidth) {
      inset = calendarRight - windowWidth;
    }

    // Make sure the calendar is inside the left side of the screen
    if (inputLeft - inset < 0) {
      inset = inputLeft;
    }

    setCalendarInset(inset);
  };

  const handleDayChange: DayPickerInputProps['onDayChange'] = (newDate: Date, { disabled }) => {
    if (disabled) return;

    if (newDate) {
      potentialValidationError = undefined;
      setValidationError(undefined);
      onChange(newDate);
      setSelectedDate(newDate);
    } else {
      potentialValidationError = `Use the format ${
        getDateTimeFormatMapForLocale(locale).DATE_SHORT
      }`;
      setSelectedDate(null);
    }
  };

  // Keyboard counterpart of the outside mousedown close above.
  const handleWrapperBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    const nextFocus = event.relatedTarget;
    if (!newCalendarInteraction && nextFocus && !event.currentTarget.contains(nextFocus)) {
      dayPickerRef.current?.hideDayPicker();
    }
  };

  const handleOutsideTouchMove = (): void => setTouchingWhileOpen(true);
  const handleOutsideTouchEnd = useCallback(
    (event: Event): void => {
      // Close if there was no scrolling
      if (!touchingWhileOpen) {
        // `dayPickerRef.getDayPicker().dayPicker` has the DOM element
        const dayPickerComponent = dayPickerRef?.current?.getDayPicker();

        if (dayPickerComponent) {
          const dayPickerEl = dayPickerComponent.dayPicker;
          const dayPickerInputEl = dayPickerRef?.current?.getInput();

          const targetIsOutsideDayPicker =
            !dayPickerEl.contains(event.target as Node) &&
            !dayPickerInputEl.contains(event.target as Node);

          if (targetIsOutsideDayPicker && dayPickerRef?.current) {
            dayPickerRef.current.hideDayPicker();
          }
        }
      }

      setTouchingWhileOpen(false);
    },
    [touchingWhileOpen]
  );

  useEffect(() => {
    if (calendarIsOpen) {
      document.addEventListener('touchend', handleOutsideTouchEnd, false);
    }
    return (): void => {
      document.removeEventListener('touchend', handleOutsideTouchEnd, false);
    };
  }, [calendarIsOpen, handleOutsideTouchEnd]);

  useEffect(() => {
    if (showCalendar && newCalendarInteraction && isMobileBrowser) {
      document.querySelector('.DayPicker-Day.DayPicker-Day--today')?.scrollIntoView(false);
    }
    if (showCalendar && onClickFromSearch) onClickFromSearch();
  }, [showCalendar]);

  const handleDayPickerShow = (): void => {
    dayPickerLateralAdjust();
    // Add a slight delay to allow the IntersectionObserver to update before we display the dropdown
    focusTimeout = setTimeout(() => setCalendarIsOpen(true), 100);
    observe();
    document.addEventListener('touchmove', handleOutsideTouchMove, false);
  };

  const handleDayPickerHide = (): void => {
    unobserve();
    setCalendarIsOpen(false);
    document.removeEventListener('touchmove', handleOutsideTouchMove, false);
  };

  const handleInputBlur = (event?: React.FocusEvent<HTMLDivElement, Element>): void => {
    onInputBlur?.(event, !!selectedDate);
    // setTimeout here ensures this.state.calendarIsOpen below accurately reflects the UI
    setTimeout(() => {
      // avoid inadvertently closing the calendar UI by only updating state when the calendar is closed
      if (potentialValidationError && !calendarIsOpen && !disableValidation) {
        setValidationError(potentialValidationError);
      }
    }, 0);
  };

  // last day in the array is the one the user selected
  const handleCalendarChange = (selectedDays: Day[]): void => {
    const addedDay = selectedDays.pop();
    if (addedDay) onChange(addedDay.date ? addedDay.date : undefined);
  };

  const handleTouchStart = (): void => {
    // `userIsTouching` temporarily sets input to readonly to avoid popping keyboard on mobile
    setUserIsTouching(true);
  };

  const showDatePicker = (): void => {
    // React 19 reattaches DayPickerInput's internal <input> ref later in the commit
    // than React 18 did. DateRangePicker calls this synchronously from the start
    // picker's day-select callback while the end picker is mid re-render, so
    // getInput() is transiently null. Defer so the ref is reattached first.
    setTimeout(() => {
      const input = dayPickerRef.current?.getInput();
      if (!input) return;
      input.focus();
      observe();
      dayPickerRef.current?.showDayPicker();
      dayPickerLateralAdjust();
    });
  };

  useImperativeHandle(externalDatepickerRef, () => ({ showDatePicker }));

  const dayPickerProps = {
    value: date,
    fromMonth: minDate,
    toMonth: maxDate,
    month: initialMonth || minDate,
    disabledDays: [
      {
        before: minDate,
        after: maxDate,
      },
      isDayBlocked,
    ],
    selectedDays: selectedDate ? [selectedDate] : [],
    navbarElement: DatePickerNavbar,
    captionElement: DatePickerCaption,
    localeUtils: MomentLocaleUtils,
    locale,
    modifiers: {
      holiday: holidays,
      ...modifiers,
    },
    renderDay: createRenderDayForHolidays(holidays || []),
  };

  const readOnly = allowKeyboardInput ? userIsTouching : true;

  function getStringFromDates(): string {
    if (selectedDate) {
      const firstDay = moment(selectedDate);
      return firstDay.format('ddd, DD MMM YYYY');
    }
    return '';
  }

  function clearDateSelection<T extends Date>(
    removedDate: T,
    handleChange: (date: Date) => void
  ): void {
    if (removedDate) {
      setSelectedDate(null);
    }
    handleChange(removedDate);
  }

  const numberOfMonthsAllowed = (): number => {
    if (maxDate) {
      // Adding 2 to the result to count both first and last month
      return Math.abs(moment().diff(moment(maxDate), 'months')) + 2;
    }
    // Adding 2 to the result to count both first and last month
    return Math.abs(moment().diff(moment(getMaxRequestDate()), 'months')) + 2;
  };

  const renderInput = (isMobile = false): React.ReactNode => {
    return (
      <Box
        onClick={(): void => {
          !isMobile && setShowCalendar(!showCalendar);
        }}
        className="multiDateAutoFillPicker-clickable-wrapper"
        maxHeight={40}
        width={isMobileBrowser && showCalendar ? '80vw' : 'unset'}
        position="relative"
        marginBottom="4x"
      >
        <InlineErrorDateInput
          inputId={id}
          style={inputStyle}
          onKeyDown={(event: KeyboardEvent): void => {
            if (event.key === 'Enter') {
              setShowCalendar(!showCalendar);
            } else if (event.key === ' ') {
              event.preventDefault();
              setShowCalendar(!showCalendar);
            }
          }}
          value={getStringFromDates()}
          errorMessage={validationError || validationErrorProp}
          placeholder={placeholder}
          aria-labelledby={ariaLabelledBy}
        />
        {isMobile && selectedDate && showCalendar ? (
          <Box
            position="absolute"
            width={sizes['4x']}
            height={sizes['4x']}
            right={sizes['4x']}
            bottom={sizes['3x']}
            onClick={(): void => clearDateSelection(selectedDate, onChange)}
          >
            <StyledCloseIcon />
          </Box>
        ) : (
          <StyledIcon iconStyle={iconStyle} />
        )}
      </Box>
    );
  };

  const renderMobile = (formGroupProps): JSX.Element => {
    return (
      <Box
        position="fixed"
        pb="0x"
        pl="0x"
        top="0"
        left="0"
        width="100vw"
        height="100vh"
        sx={{
          backgroundColor: background.overlay,
        }}
        zIndex="10000"
        display="flex"
        flexDirection="column"
        justifyContent="flex-end"
      >
        <CalendarBottomSheet reference={wrapperRef} onClose={() => setShowCalendar(false)}>
          <Heading size="200" mt="8x" mb="1x">
            {serviceName as string}
          </Heading>
          <Paragraph size="100" mb="6x" textAlign="center" webProps={{ maxWidth: '80vw' }}>
            {serviceSubtitle as string}
          </Paragraph>
          {renderInput(true)}
          <Box
            sx={{
              overflow: 'scroll',
            }}
          >
            <DayPicker
              {...formGroupProps}
              {...dayPickerProps}
              onDayClick={handleDayChange}
              ref={dayPickerRef}
              canChangeMonth={false}
              numberOfMonths={numberOfMonthsAllowed()}
            />
          </Box>
          <Box
            position="fixed"
            bottom="0"
            padding="2x"
            paddingBottom="6x"
            display="flex"
            flexDirection="row"
            justifyContent="center"
            sx={{
              backgroundColor: background.primary,
            }}
            width="100vw"
            height="64px"
            zIndex="30"
          >
            <Button
              sx={{
                color: textColor.primary_inverse,
                backgroundColor: colors.interactive_link.primary_base,
                p: '0x',
              }}
              fullWidth
              onClick={(): void => {
                const error = checkDateErrors(selectedDate, i18n);
                if (!disableValidation) setValidationError(error);
                if (!error) {
                  setShowCalendar(false);
                }
              }}
            >
              {t`Save dates`}
            </Button>
          </Box>
        </CalendarBottomSheet>
      </Box>
    );
  };

  const renderDesktop = (formGroupProps): JSX.Element => {
    return (
      <Box
        pb="0x"
        mt="5x"
        sx={{ backgroundColor: 'white' }}
        width="640px"
        border={`${borderWidth.primary} solid ${colors.border.primary}`}
        borderRadius={radii.secondary}
      >
        <DayPicker
          {...formGroupProps}
          {...dayPickerProps}
          onDayClick={handleDayChange}
          ref={dayPickerRef}
          numberOfMonths={2}
        />
      </Box>
    );
  };

  const dayPickerInputProps = {
    value: date,
    onDayChange: handleDayChange,
    onDayPickerShow: handleDayPickerShow,
    onDayPickerHide: handleDayPickerHide,
    format: getDateTimeFormatMapForLocale(locale).DATE_SHORT,
    formatDate: MomentLocaleUtils.formatDate,
    parseDate: MomentLocaleUtils.parseDate,
    dayPickerProps,
    inputProps: {
      autoComplete: 'off',
      id,
      onBlur: handleInputBlur,
      onTouchStart: handleTouchStart,
      readOnly,
    },
  };

  if (asCalendar) {
    const selectedDays = date ? [dayFactory({ date })] : [];

    return (
      <MultiDatePicker
        {...other}
        {...dayPickerProps}
        selectedDays={selectedDays}
        onChange={handleCalendarChange}
        disableBeforeDateTime={minDate}
        disableAfterDateTime={maxDate || undefined}
        holidays={holidays}
        locale={locale}
        validationError={validationErrorProp}
        validationType={validationType}
        serviceSubtitle={serviceSubtitle}
        serviceName={serviceName}
        newCalendarInteraction={newCalendarInteraction}
      />
    );
  }

  return (
    <LabelAndErrorFormGroup
      {...other}
      useLabelElementAsCaption={false}
      validationError={parseDatePopoverErrors(
        validationError || validationErrorProp,
        newCalendarInteraction
      )}
      validationType={validationType}
    >
      {({ ...formGroupProps }): JSX.Element => {
        // In some cases, we need the `id` attribute generated from the `LabelAndErrorFormGroup`
        dayPickerInputProps.inputProps.id = dayPickerInputProps.inputProps.id || formGroupProps.id;

        return (
          <DatePickerStylingWrapper
            className={className}
            readOnly={readOnly}
            styleAsError={!!validationError || !!validationErrorProp}
            calendarDropUp={calendarDirection === CalendarDirection.UP || calendarDropUp}
            ref={isMobileBrowser ? null : wrapperRef}
            onBlur={handleWrapperBlur}
            calendarHeightOffset={calendarHeightOffset}
            calendarInset={calendarInset}
            calendarIsOpen={calendarIsOpen}
            newCalendarInteraction={newCalendarInteraction}
            isMobileBrowser={newCalendarInteraction && isMobileBrowser}
          >
            {newCalendarInteraction && renderInput()}
            {newCalendarInteraction &&
              showCalendar &&
              isMobileBrowser &&
              renderMobile(formGroupProps)}
            {newCalendarInteraction &&
              showCalendar &&
              !isMobileBrowser &&
              renderDesktop(formGroupProps)}
            {!newCalendarInteraction && (
              <DayPickerInput
                {...formGroupProps}
                {...dayPickerInputProps}
                placeholder={placeholder}
                inputProps={{
                  ...dayPickerInputProps.inputProps,
                  'aria-label': t({
                    message: `date input in ${
                      getDateTimeFormatMapForLocale(locale).DATE_SHORT
                    } format`,
                    comment:
                      "The var here will be a localized date string format, ex: 'date input in MM/DD/YYYY format'",
                  }),
                }}
                ref={dayPickerRef}
                keepFocus={false}
              />
            )}
            {!newCalendarInteraction && <StyledIcon onClick={showDatePicker} />}
          </DatePickerStylingWrapper>
        );
      }}
    </LabelAndErrorFormGroup>
  );
});

export default DatePicker;
